import { withNamespaces } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    Button,
    Card,
    CardBody,
    Nav,
    NavItem,
    NavLink,
} from 'reactstrap';

import { ROUTE_PATH_ADMIN_GUARD_FREIGHT_DETAILS } from 'src/modules/3rd-party-management/constants';
import { getCountryFlag } from '../../../../constants';
import { BidStatuses } from '../../constants';
import { BidSummaryTabs } from '../constants';
import VerificationStatusBanner from './VerificationStatusBanner';
import VerificationStepper from './VerificationStepper';

const BidHeader = ({
    activeTab,
    bid,
    onTabChange,
    t,
}) => {
    const history = useHistory();
    const status = BidStatuses[bid.status];
    const freightDetailsPath = ROUTE_PATH_ADMIN_GUARD_FREIGHT_DETAILS.replace(':freightId', bid.freightId);

    return (
        <Card>
            <CardBody className="pb-0">
                <Button
                    className="mb-3 p-0"
                    color="link"
                    onClick={() => history.push(`${freightDetailsPath}?tab=bids`)}
                    type="button"
                >
                    <i className="ri-arrow-left-line align-middle me-1" />
                    {t('Back to all bids')}
                </Button>

                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                    <div className="d-flex align-items-start">
                        <img
                            alt={bid.carrier}
                            className="avatar-sm rounded-circle me-3"
                            src={bid.logo}
                        />

                        <div>
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                <h4 className="mb-0 fw-bold">{bid.carrier}</h4>
                                <span className={`badge p-2 ${status.className}`}>
                                    <i className={`${status.icon} align-middle me-1`} />
                                    {t(status.title)}
                                </span>
                            </div>

                            <p className="text-muted fw-normal mb-2">
                                {`${t('Bid for Freight')} ${bid.freightReference}`}
                            </p>

                            <div className="d-flex flex-wrap align-items-center gap-3 text-muted fw-normal">
                                <span className="d-flex align-items-center gap-1">
                                    <img
                                        alt={bid.country}
                                        className="country-flag"
                                        src={getCountryFlag(bid.country)}
                                    />
                                    {bid.country}
                                </span>
                                <span>{`${t('Established')} ${bid.established}`}</span>
                                <span className="fw-medium text-body">
                                    {new Intl.NumberFormat('de-DE', {
                                        style: 'currency',
                                        currency: 'EUR',
                                        maximumFractionDigits: 0,
                                    }).format(bid.amount)}
                                </span>
                                <span>
                                    <i className="ri-calendar-line align-middle me-1" />
                                    {`${bid.deliveryDays} ${t('days delivery')}`}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap gap-4">
                        <div>
                            <span className="fw-bold d-block">{t('Submitted')}</span>
                            <span className="fw-medium">{bid.submittedAt}</span>
                        </div>
                        {bid.completedAt && (
                            <div>
                                <span className="fw-bold d-block">{t('Verification completed')}</span>
                                <span className="fw-medium">{bid.completedAt}</span>
                            </div>
                        )}
                        {bid.lastUpdate && (
                            <div>
                                <span className="fw-bold d-block">{t('Last update')}</span>
                                <span className="fw-medium">{bid.lastUpdate}</span>
                            </div>
                        )}
                        {bid.duration && (
                            <div>
                                <span className="fw-bold d-block">{t('Duration')}</span>
                                <span className="fw-medium">{bid.duration}</span>
                            </div>
                        )}
                        {bid.estimatedCompletion && (
                            <div>
                                <span className="fw-bold d-block">{t('Est. completion')}</span>
                                <span className="fw-medium">{bid.estimatedCompletion}</span>
                            </div>
                        )}
                    </div>
                </div>

                <VerificationStatusBanner outcome={bid.outcome} />
                <VerificationStepper steps={bid.steps} />

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <Nav className="nav-tabs-custom" tabs>
                        {BidSummaryTabs.map((tab) => (
                            <NavItem key={tab.key}>
                                <NavLink
                                    className={activeTab === tab.key ? 'active' : ''}
                                    onClick={() => onTabChange(tab.key)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t(tab.title)}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>

                    <Button className="mb-2" color="secondary" outline type="button">
                        <i className="ri-download-2-line align-middle me-1" />
                        {t('Download Report')}
                    </Button>
                </div>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(
    BidHeader
);
